<template>
  <section class="md:my-24 mt-3 grid gap-y-5">
    <div id="organizations-header" class="flex items-center justify-between">
      <h4>
        {{ $t("labels.generic", { pt: "Organizações", en: "Organizations" }) }}
      </h4>
      <Button
        class="btn-primary p-button-sm"
        id="btn-add-organizations"
        @click="go.redirectTo('formsOrganizations')"
      >
        <v-icon name="md-add"></v-icon>
        <p class="ml-2">
          {{
            $t("labels.generic", {
              pt: "Cadastrar organizações",
              en: "Add organizations",
            })
          }}
        </p>
      </Button>
    </div>
    <div
      id="organization-div-table"
      class="bg-white rounded-lg border border-gray-300"
    >
      <Table
        :slotData="organizations"
        :columns="columns"
        :actions="actions"
        :loading="loading"
        :params="params"
        :filters="filters"
        :hasStatus="true"
        @onEdit="(e) => goToEdit(e)"
        @onTheme="(e) => goToTheme(e)"
        @onChangeStatus="(e) => confirmModal(e)"
        @onPaginate="paginate($event)"
        @onToggleFilter="(e, type) => toggleFilter(e, type)"
        @onFilter="filterFor()"
        @onClearFilter="clearFilter()"
      >
        <template #filters>
          <InputText v-if="filterType == 'name'" v-model="filters.name" />
        </template>
      </Table>
    </div>
    <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }" />
  </section>
</template>
<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { OrganizationsRest } from "@/services/rest/organizations.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import {
  organizationActions,
  organizationColumns,
  organizationDropdown,
} from "./organization.util";
import { OrganizationsService } from "./organizations.service";

export default defineComponent({
  name: "organizations",

  data() {
    return {
      filters: {
        isCleareable: false,
        name: null,
        status: organizationDropdown,
      } as any,
      columns: organizationColumns,
      actions: organizationActions,
      filterType: "" as string,
      params: new QueryParams(),
      loading: true as boolean,
      organizations: [] as any[],
    };
  },
  mounted() {
    this.organizationsPaginated();
  },
  computed: {
    service(): OrganizationsService {
      return new OrganizationsService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    rest(): OrganizationsRest {
      return new OrganizationsRest();
    },
  },
  methods: {
    goToEdit(event: any): void {
      this.go.redirectToPage("formsOrganizations", {
        organizationId: event.id,
      });
    },
    goToTheme(event: any): void {
      this.go.redirectToPage("theme", {
        id: event.id,
      });
    },
    changeOrganizationStatus(organization: any): void {
      this.loading = true;
      organization.status = this.service.statusChange(
        organization.status?.value
      );
      this.rest.putOrganization(organization.id, organization).subscribe({
        next: (response) => {
          this.loading = false;
          var index = this.organizations.findIndex(
            (e: any) => e.id == response.id
          );
          response.status = this.service.remakeObjectStatus(response.status);
          this.organizations[index] = response;
          this.$toast.add(ToastFactory.success(Messages.USER_STATUS));
        },
      });
    },
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      params.name = this.filters.name;
      params.role = this.filters.role?.value;
      params.organization = this.filters.organization;
      params.status = this.filters.status?.value;
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.loading = true;
      this.service.organizations.pipe(delay(500)).subscribe({
        next: (response) => {
          this.loading = false;
          this.organizations = response.content;
          this.params.total = response.total;
        },
      });
      this.service.getOrganizations(queryParams);
    },
    organizationsPaginated(): void {
      const page = this.params.page;
      const size = this.params.size;
      const params = new QueryParams(page, size);
      this.setPage(params);
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      var params = new QueryParams();
      params.name = this.filters.name;
      this.setPage(params);
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.filters.name = null;
      this.organizationsPaginated();
    },
    isCleareable(): boolean {
      return this.filters.name !== "" || this.filters.status.selected !== null;
    },
    confirmModal(event?: any): void {
      const check = event.status.value === "ACTIVATED";
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: check
            ? `Ao desativar a organização “${event.name}”, todos os usuários associados a ela perderão o acesso a plataforma.`
            : `Ao ativar, todos os usuários associados voltarão a ter acesso à plataforma normalmente.`,
          en: `By deactivating the organization "${event.name}", all users associated with it will lose access to the platform.`,
        }),
        header: this.$t("labels.generic", {
          pt: check ? "Desativar organização?" : "Ativar organização?",
          en: "Deactivate organization?",
        }),
        acceptLabel: check ? "Desativar organização" : "Ativar organização",
        rejectLabel: "Cancelar",
        rejectClass: "btn-outline",
        acceptClass: check ? "btn-delete-block" : "btn-primary",
        accept: () => {
          this.changeOrganizationStatus(event);
        },
      });
    },
  },
});
</script>
